import { Stack, Typography } from '@mui/material';
import PaiAutoComplete, { OptionType } from 'features/ui/base-components/pai-auto-complete';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import { BasicActionDialog } from 'features/ui/base-dialogs';
import { CompanyInfo } from 'hooks/use-own-company';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { CompanyErrors } from './payment.types';

interface Props {
  open: boolean;
  error: CompanyErrors | undefined;
  loading: boolean;
  companyInfo: CompanyInfo;
  countryOptions: OptionType[];

  handleCompanyInfoChange(field: keyof CompanyInfo): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleCountryChange(event: React.ChangeEvent<any>, newValue: OptionType): void;

  handleClose(): void;

  handleUpdateCompanyInfo(): void;
}

const CompanyInfoMissingDialog: React.FC<Props> = ({
  open,
  error,
  loading,
  companyInfo,
  countryOptions,
  handleCompanyInfoChange,
  handleCountryChange,
  handleClose,
  handleUpdateCompanyInfo,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const { name, street, zip, city, country } = companyInfo;

  const isCompanyNameTooLong = error === CompanyErrors.COMPANY_NAME_TOO_LONG || name.length >= 30;
  const isCompanyAddressMissing = error === CompanyErrors.COMPANY_ADDRESS_MISSING;

  const errorText = isCompanyNameTooLong ? t('companyNameTooLong') : isCompanyAddressMissing ? t('companyAddressMissing') : undefined;

  return (
    <BasicActionDialog
      open={open}
      dialogTitle={t('warningCompanyInfoMissing')}
      maxWidth={'xs'}
      cancelButtonText={t('later')}
      proceedButtonText={t('update')}
      handleClose={handleClose}
      handleProceed={handleUpdateCompanyInfo}
      loading={loading}
      proceedButtonDisabled={!name || !street || !zip || !city || _.isNil(country) || _.isNil(country.value) || name.length >= 30}
    >
      <Stack gap={2}>
        <Typography className={classes.text}>{t('warningCompanyInfoMissingContent')}</Typography>
        <PaiTextField
          value={name}
          onChange={handleCompanyInfoChange('name')}
          label={t('nameOfCompany')}
          placeholder={t('nameOfCompany')}
          error={isCompanyNameTooLong || (isCompanyAddressMissing && !name)}
          errorText={errorText}
        />
        <PaiTextField
          value={street}
          onChange={handleCompanyInfoChange('street')}
          label={t('street')}
          placeholder={t('street')}
          error={isCompanyAddressMissing && !street}
          errorText={errorText}
        />
        <PaiTextField
          value={zip}
          onChange={handleCompanyInfoChange('zip')}
          label={t('zip')}
          placeholder={t('zip')}
          error={isCompanyAddressMissing && !zip}
          errorText={errorText}
        />
        <PaiTextField
          value={city}
          onChange={handleCompanyInfoChange('city')}
          label={t('city')}
          placeholder={t('city')}
          error={isCompanyAddressMissing && !city}
          errorText={errorText}
        />
        <PaiAutoComplete
          options={countryOptions}
          selected={country}
          label={t('country')}
          onChange={handleCountryChange}
          disableClearable={true}
          error={isCompanyAddressMissing && !country}
          errorText={errorText}
        />
      </Stack>
    </BasicActionDialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  text: {
    fontSize: theme.typography.pxToRem(13),
  },
}));

export default CompanyInfoMissingDialog;
